import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import './App.css';
import './styles/design-system.css';

// Import components
import PersonalityQuiz from './PersonalityQuiz';
import FeedPage from './pages/FeedPage';
import MessagesPage from './pages/MessagesPage';
import ProfilePage from './pages/ProfilePage';
import BlogPage from './pages/BlogPage';
import BlogPost from './pages/BlogPost';
import TravelGroupsPage from './pages/TravelGroupsPage';
import TravelInfoPage from './pages/TravelInfoPage';
import AdvertisingPage from './pages/AdvertisingPage';
import AdminPage from './pages/AdminPage';
import MatchesPage from './pages/MatchesPage';
import PaymentIntegration from './components/PaymentIntegration';
import CookieConsent from './components/CookieConsent';
import EmailMarketing from './components/EmailMarketing';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import MeetupsInfoPage from './pages/MeetupsInfoPage';
import LocalMeetsPage from './pages/LocalMeetsPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import ContactUs from './pages/ContactUs';
import FAQPage from './pages/FAQPage';
import EmailVerification from './pages/EmailVerification';
// import { getCurrentUser, signOut } from 'aws-amplify/auth';
import PricingPage from './pages/PricingPage';
import PaymentPage from './pages/PaymentPage';
import AIMarketingAgent from './components/AIMarketingAgent';
import MobileAppPromotion from './components/MobileAppPromotion';
import AIIntegrationManager from './components/AIIntegrationManager';
import SMSTest from './components/SMSTest';

// Import layouts
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showCookieConsent, setShowCookieConsent] = useState(true);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState('free');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        // Temporarily disable AWS Amplify auth for static hosting
        // const currentUser = await getCurrentUser();
        // setUser(currentUser);
        // setIsAuthenticated(true);
        setIsAuthenticated(false);
        setUser(null);
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    checkAuthState();

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      // await signOut();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  // Homepage Component
  const HomePage = () => (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat" 
               style={{
                 backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/sebastian-pena-lambarri-7i5HMCGupVw-unsplash.jpg')`,
                 backgroundColor: '#eaf6fb'
               }}>
        <div className="container text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-shadow-lg">
            Find Your Perfect Travel Companion
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-shadow-md leading-relaxed">
            Connect with like-minded travelers based on personality compatibility. 
            Make every journey safer, more enjoyable, and truly memorable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="bg-blue-600 text-white text-lg px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors">
              Get Started
            </Link>
            <Link to="/about" className="bg-white text-blue-600 text-lg px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors">
              Learn More
            </Link>
          </div>
          
          {/* Personality Test CTA */}
          <div className="absolute bottom-8 right-8 bg-white/95 text-gray-900 p-6 rounded-xl shadow-2xl backdrop-blur-sm border border-white/20 max-w-xs">
            <h3 className="text-lg font-semibold mb-2 text-blue-600">Take Our Personality Test</h3>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Discover your travel personality and find compatible companions
            </p>
            <Link to="/personality-test" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm">
              Start Test
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Why Choose GradedWash?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our personality-based matching system ensures you connect with travelers who share your interests, 
              travel style, and safety preferences.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl">
                🧠
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Personality Matching</h3>
              <p className="text-gray-600 leading-relaxed">
                Our advanced algorithm matches you with travelers based on personality compatibility, 
                travel preferences, and safety concerns.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl">
                🛡️
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Safety First</h3>
              <p className="text-gray-600 leading-relaxed">
                Verified profiles, background checks, and community reviews ensure you travel with 
                trustworthy companions.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl">
                🌍
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Global Community</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect with travelers from around the world, share experiences, and build lasting friendships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-xl mb-8 opacity-90">
            Take our personality quiz and find your perfect travel companion today.
          </p>
          <Link to="/signup" className="bg-white text-blue-600 text-lg px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors">
            Start Your Journey
          </Link>
        </div>
      </section>
    </div>
  );

  return (
    <Router>
      <div className="App">
        {/* Professional Navigation */}
        <nav style={{
          backgroundColor: 'white',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid #e5e7eb',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          padding: '0'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '70px'
          }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Link to="/" style={{ 
                textDecoration: 'none', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px' 
              }}>
                <img
                  src="/gradedwash-logo.svg"
                  alt="GradedWash Logo"
                  style={{ height: '50px', width: 'auto' }}
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            {windowWidth > 768 && (
              <div style={{ 
                display: 'flex',
                alignItems: 'center', 
                gap: '32px'
              }}>
                {isAuthenticated ? (
                  <>
                    <Link to="/feed" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Feed</Link>
                    <Link to="/matches" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Matches</Link>
                    <Link to="/messages" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Messages</Link>
                    <Link to="/profile" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Profile</Link>
                    <Link to="/personality-test" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Retake Test</Link>
                    <button
                      onClick={handleLogout}
                      style={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Home</Link>
                    <Link to="/blog" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Blog</Link>
                    <Link to="/travel-groups" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Travel Groups</Link>
                    <Link to="/travel-info" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Travel Info</Link>
                    <Link to="/local-meets" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Local Meets</Link>
                    <Link to="/advertising" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Advertise</Link>
                    <Link to="/about" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>About</Link>
                    <Link to="/contact" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>Contact</Link>
                    <Link to="/faq" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>FAQ</Link>
                    <Link to="/login" style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '16px',
                      transition: 'all 0.2s'
                    }}>
                      Sign In
                    </Link>
                  </>
                )}
              </div>
            )}

            {/* Mobile Menu Button */}
            {windowWidth <= 768 && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '8px',
                  color: '#374151'
                }}
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? '✕' : '☰'}
              </button>
            )}
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && windowWidth <= 768 && (
            <div style={{
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {isAuthenticated ? (
                  <>
                    <Link to="/feed" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Feed</Link>
                    <Link to="/matches" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Matches</Link>
                    <Link to="/messages" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Messages</Link>
                    <Link to="/profile" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Profile</Link>
                    <Link to="/personality-test" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Retake Test</Link>
                    <button
                      onClick={handleLogout}
                      style={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        padding: '16px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '18px',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Home</Link>
                    <Link to="/blog" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Blog</Link>
                    <Link to="/travel-groups" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Travel Groups</Link>
                    <Link to="/travel-info" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Travel Info</Link>
                    <Link to="/local-meets" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Local Meets</Link>
                    <Link to="/advertising" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Advertise</Link>
                    <Link to="/about" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>About</Link>
                    <Link to="/contact" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>Contact</Link>
                    <Link to="/faq" style={{ 
                      color: '#374151', 
                      textDecoration: 'none', 
                      fontWeight: '600', 
                      fontSize: '18px', 
                      padding: '16px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s'
                    }}>FAQ</Link>
                    <Link to="/login" style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      padding: '16px 20px',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '18px',
                      textAlign: 'center',
                      display: 'block',
                      transition: 'all 0.2s'
                    }}>
                      Sign In
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>

        {/* Routes */}
        <Routes>
          <Route path="/" element={<HomePage />} />
          
          {/* Authentication Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          
          {/* Protected Routes */}
          <Route path="/feed" element={
            <ProtectedRoute>
              <FeedPage />
            </ProtectedRoute>
          } />
          <Route path="/matches" element={
            <ProtectedRoute>
              <MatchesPage />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/personality-test" element={
            <ProtectedRoute>
              <PersonalityQuiz />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
          
          {/* Public Pages */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/travel-groups" element={<TravelGroupsPage />} />
          <Route path="/travel-info" element={<TravelInfoPage />} />
          <Route path="/local-meets" element={<LocalMeetsPage />} />
          <Route path="/meetups-info" element={<MeetupsInfoPage />} />
          <Route path="/advertising" element={<AdvertisingPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* SMS Test Route */}
          <Route path="/sms-test" element={<SMSTest />} />
        </Routes>

        {/* Components */}
        <PaymentIntegration />
        {showCookieConsent && <CookieConsent />}
        <EmailMarketing />
        <AIMarketingAgent />
        <MobileAppPromotion />
        <AIIntegrationManager />
      </div>
    </Router>
  );
}

export default App;

